<template>
  <ion-page>
    <!-- Fixed header -->
    <div class="fixed-header">
      <b-slide-header />
    </div>

    <ion-content
      ref="contentRef"
      :fullscreen="false"
      :scroll-y="true"
      :scroll-events="true"
      class="satisfactions-page-content"
    >
      <!-- Home button -->
      <ion-fab
        slot="fixed"
        horizontal="end"
        vertical="top"
        class="top-navigation"
      >
        <a href="/evaluate">
          <ion-fab-button color="light">
            <ion-icon :icon="homeSharp" />
          </ion-fab-button>
        </a>
      </ion-fab>

      <!-- Loading indicators -->
      <ion-loading
        :is-open="loading"
        message="Hämtar värden..."
        class="custom-loading"
      />
      <ion-loading
        :is-open="saving"
        message="Sparar dina val..."
        class="custom-loading"
      />

      <!-- Main content area -->
      <div class="content-wrapper">
        <div class="content-area">
          <div class="page-header">
            <h1 class="page-title">Områden som jag är nöjd med</h1>
            <p class="subtitle" v-if="!loading">
              Välj de <strong>{{ MAX_SELECTIONS }}</strong> områden som du är
              <strong>mest nöjd</strong> med.
            </p>
          </div>

          <!-- Loading state -->
          <div v-if="loading" class="loading-state">
            <div class="loading-indicator">
              <span class="loading-icon"></span>
              <span class="loading-text">Laddar dina alternativ...</span>
            </div>
          </div>

          <!-- Empty state -->
          <div v-else-if="satisfactions.length === 0" class="empty-state">
            <div class="empty-icon">
              <ion-icon
                :icon="checkmarkCircle"
                color="medium"
                class="text-6xl"
              ></ion-icon>
            </div>
            <p class="empty-text">
              Du har inte markerat något område som du är nöjd med ännu.
            </p>
          </div>

          <!-- Selection area -->
          <template v-else>
            <div class="selection-area">
              <!-- Selection counter -->
              <div class="selection-counter">
                <span class="counter-text" v-if="!canProceed">
                  <!-- Välj {{ remainingSelections }} till -->
                </span>
                <span class="counter-text counter-complete-text" v-else>
                  Perfekt! Du kan nu gå vidare
                </span>
              </div>

              <!-- Satisfaction options -->
              <ul class="satisfaction-list">
                <li
                  v-for="factor in satisfactions"
                  :key="factor.category_id"
                  class="satisfaction-item"
                >
                  <label
                    :for="`satisfaction-${factor.category_id}`"
                    class="satisfaction-label"
                    :class="{ selected: selected.includes(factor.category_id) }"
                  >
                    <input
                      type="checkbox"
                      :id="`satisfaction-${factor.category_id}`"
                      :value="factor.category_id"
                      :checked="selected.includes(factor.category_id)"
                      @change="toggleSelection"
                      class="satisfaction-checkbox"
                    />
                    <span class="checkbox-custom"></span>
                    <span class="satisfaction-text">{{ factor.label }}</span>
                  </label>
                </li>
              </ul>

              <p class="hint-text">
                Klicka på ett markerat val för att avmarkera och välja ett
                annat.
              </p>
            </div>
          </template>

          <!-- Spacer for navigation area -->
          <div class="navigation-spacer"></div>
        </div>
      </div>

    </ion-content>

    <!-- Fixed footer navigation (outside ion-content for proper fixed positioning on PC) -->
    <div class="navigation-area">
      <!-- Scroll down button -->
      <div
        class="scroll-button"
        v-if="showScrollButton"
        @click="scrollDown"
      >
        <b-icon-button>
          <arrow-down />
        </b-icon-button>
      </div>

      <!-- Navigation buttons -->
      <div class="grid grid-cols-2 gap-4 max-w-lg mx-auto" style="position: relative; z-index: 15;">
        <b-button @click="prev" look="secondary" class="back-button"
          >Tillbaka</b-button
        >
        <b-button
          @click="save"
          class="next-button"
          :class="{ 'button-enabled': canProceed }"
          :style="{ pointerEvents: canProceed ? 'auto' : 'none' }"
        >
          Nästa
        </b-button>
      </div>
    </div>
  </ion-page>
</template>
<script setup lang="ts">
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonLoading,
  IonPage,
  IonIcon,
  useIonRouter,
  toastController,
  alertController,
} from '@ionic/vue';
import BSlideHeader from '@/components/BSlideHeader.vue';
import {
  computed,
  provide,
  ref,
  onMounted,
  nextTick,
  onBeforeUnmount,
  watch,
} from 'vue';
import { homeSharp, checkmarkCircle } from 'ionicons/icons';
import { useStore } from '@/stores/main';
import BIconButton from '@/components/BIconButton.vue';
import ArrowDown from '@/components/icons/ArrowDown.vue';
import Flag from '@/components/illustrations/Flag.vue';

const router = useIonRouter();
const store = useStore();

interface Satisfaction {
  selected: number;
  category_id: number;
  label: string;
}

const satisfactions = ref<Satisfaction[]>([]);
const loading = ref(false);
const saving = ref(false);
const selected = ref<number[]>([]);
const MAX_SELECTIONS = 3;
const contentRef = ref<HTMLIonContentElement | null>(null);
const showScrollButton = ref(false);

const fetchData = async () => {
  loading.value = true;
  try {
    satisfactions.value = await store.fetchSatisfaction();
    selected.value = satisfactions.value
      .filter(f => f.selected === 1)
      .map(factor => factor.category_id);
  } catch (error) {
    presentToast('Kunde inte hämta data. Försök igen.', 'danger');
  } finally {
    loading.value = false;
    await nextTick();
    setTimeout(() => {
      checkScrollNeeded();
    }, 200);
  }
};

const checkScrollNeeded = () => {
  if (contentRef.value) {
    const contentEl = contentRef.value as any;
    const scrollEl = contentEl.$el?.shadowRoot?.querySelector('.inner-scroll') ?? contentEl.shadowRoot?.querySelector('.inner-scroll');
    if (scrollEl) {
      showScrollButton.value = scrollEl.scrollHeight > scrollEl.clientHeight + 50;
    }
  }
};

const scrollDown = () => {
  if (contentRef.value) {
    contentRef.value.scrollToBottom(300);
  }
};

// Watch for content changes to update scroll button visibility
watch(satisfactions, () => {
  nextTick(() => {
    setTimeout(() => {
      checkScrollNeeded();
    }, 100);
  });
});

onMounted(() => {
  fetchData();
  window.addEventListener('resize', checkScrollNeeded);
  if (contentRef.value) {
    const contentEl = contentRef.value as any;
    const scrollEl = (contentEl.$el?.shadowRoot ?? contentEl.shadowRoot)?.querySelector('.inner-scroll');
    if (scrollEl) {
      scrollEl.addEventListener('scroll', checkScrollNeeded);
    }
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScrollNeeded);
  if (contentRef.value) {
    const contentEl = contentRef.value as any;
    const scrollEl = (contentEl.$el?.shadowRoot ?? contentEl.shadowRoot)?.querySelector('.inner-scroll');
    if (scrollEl) {
      scrollEl.removeEventListener('scroll', checkScrollNeeded);
    }
  }
});

const selectedCount = computed(() => selected.value.length);
const canProceed = computed(() => selectedCount.value >= 1 && selectedCount.value <= MAX_SELECTIONS);
const remainingSelections = computed(
  () => MAX_SELECTIONS - selectedCount.value,
);

async function presentToast(message: string, color = 'success') {
  const toast = await toastController.create({
    message,
    duration: 2000,
    position: 'bottom',
    color,
  });
  return toast.present();
}

function toggleSelection(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value, 10);
  if (selected.value.includes(value)) {
    selected.value = selected.value.filter(v => v !== value);
  } else {
    if (selectedCount.value < MAX_SELECTIONS) {
      selected.value.push(value);
    } else {
      target.checked = false;
      alertController.create({
        header: 'Max antal uppnått',
        message: 'Du har redan valt 3 områden. Avmarkera ett område först om du vill välja ett annat.',
        buttons: ['OK'],
        cssClass: 'custom-alert',
      }).then(alert => alert.present());
    }
  }
}

const goHome = () => router.navigate('/evaluate', 'root', 'replace');
const prev = () => router.replace('/goals/reminder');

async function save() {
  if (!canProceed.value) {
    const alert = await alertController.create({
      header: 'Välj 3 alternativ',
      message: `Du måste välja exakt ${MAX_SELECTIONS} områden du är nöjd med innan du kan gå vidare. Du har valt ${selectedCount.value} alternativ.`,
      buttons: ['OK'],
      cssClass: 'custom-alert',
    });
    await alert.present();
    return;
  }
  saving.value = true;
  try {
    await store.updateSatisfaction(selected.value.map(String));
    router.push('/goals/reflect');
  } catch (error) {
    presentToast('Kunde inte spara dina val. Försök igen.', 'danger');
  } finally {
    saving.value = false;
  }
}

provide('pageData', { number: 3, title: 'Mina mål', icon: Flag });
provide('colorScheme', 'blue');
</script>

<style scoped>
/* Fixed header styles */
.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  background-color: #f3fffe;
  height: 80px;
}

/* Ionic content styles */
.satisfactions-page-content {
  --background: #f3fffe;
  --padding-top: 80px;
  --padding-bottom: 120px; /* Space for navigation area */
  --overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

/* Fix for iOS scrolling */
ion-content::part(scroll) {
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
  height: 100%;
  padding-bottom: 120px; /* Match ion-content padding */
}

/* Top navigation */
.top-navigation {
  margin-top: 90px;
  margin-right: 1rem;
  z-index: 100;
}

/* Content wrapper - no scrolling, just a container */
.content-wrapper {
  min-height: 100%;
  position: relative;
}

/* Content area */
.content-area {
  padding: 20px 2rem 0;
}

/* Page header */
.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 0.75rem;
}

.subtitle {
  font-size: 1.125rem;
  color: #4a6572;
  line-height: 1.5;
}

/* Loading and empty states */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  text-align: center;
}

.loading-indicator,
.empty-icon {
  margin-bottom: 1rem;
}

.loading-icon {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(65, 184, 175, 0.3);
  border-radius: 50%;
  border-top-color: #41b8af;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text,
.empty-text {
  font-size: 1rem;
  color: #4a6572;
}

/* Selection area */
.selection-area {
  margin-top: 1rem;
}

.selection-counter {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.counter-text {
  font-size: 0.875rem;
  color: #4a6572;
}

.counter-complete-text {
  color: #00897b;
  font-weight: 500;
}

/* Satisfaction list */
.satisfaction-list {
  list-style: none;
  padding: 0;
  margin: 1rem 0 2rem 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

@media (min-width: 1024px) {
  .satisfaction-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

.satisfaction-item {
  width: 100%;
}

.satisfaction-label {
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  -webkit-user-select: none;
  user-select: none;
}

.satisfaction-label:hover {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.satisfaction-label.selected {
  background-color: #e0f7fa;
  border: 1px solid #4fc3f7;
}

.satisfaction-checkbox {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-custom {
  height: 1.25rem;
  width: 1.25rem;
  background-color: #eee;
  border-radius: 4px;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.satisfaction-checkbox:checked ~ .checkbox-custom {
  background-color: #0288d1;
}

.checkbox-custom:after {
  content: '';
  display: none;
  width: 0.5rem;
  height: 0.5rem;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.satisfaction-checkbox:checked ~ .checkbox-custom:after {
  display: block;
}

.satisfaction-text {
  font-size: 1rem;
  color: #2c3e50;
  flex: 1;
}

.hint-text {
  font-size: 0.875rem;
  color: #78909c;
  margin-top: 1rem;
  margin-bottom: 2rem;
  font-style: italic;
}

/* Navigation spacer */
.navigation-spacer {
  height: 120px;
}

/* Navigation area */
.navigation-area {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem 2rem;
  background-color: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 100;
}

@media (min-width: 1024px) {
  .navigation-area {
    background-color: #a1cdc5;
  }
}

.scroll-button {
  position: absolute;
  bottom: 100%;
  right: 2rem;
  margin-bottom: 1rem;
  z-index: 11;
}

@media (min-width: 768px) {
  .scroll-button {
    right: 3rem;
  }
}

@media (min-width: 1024px) {
  .scroll-button {
    right: 4rem;
  }
}

.next-button {
  opacity: 0.7;
  transition: all 0.3s ease;
  pointer-events: auto !important;
  z-index: 20;
  position: relative;
}

.next-button.button-enabled {
  opacity: 1;
  cursor: pointer;
}

.next-button:disabled {
  cursor: not-allowed;
  pointer-events: none;
}

.custom-loading {
  --background: rgba(255, 255, 255, 0.9);
  --spinner-color: #0288d1;
  --text-color: #2c3e50;
}

/* Prevent accidental highlighting */
* {
  -webkit-tap-highlight-color: transparent;
}

/* Ensure inputs work properly */
input[type="checkbox"] {
  -webkit-appearance: none;
  appearance: none;
}

/* Custom alert styling */
:deep(.custom-alert) {
  --background: white;
  --max-width: 90%;
  --width: 400px;
}

:deep(.custom-alert .alert-head) {
  padding: 1.5rem 1.5rem 0.75rem;
}

:deep(.custom-alert .alert-title) {
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 500;
}

:deep(.custom-alert .alert-message) {
  color: #4a6572;
  font-size: 1rem;
  line-height: 1.5;
  padding: 0.75rem 1.5rem 1.5rem;
}

:deep(.custom-alert .alert-button) {
  color: #0288d1;
  font-weight: 500;
}

:deep(.custom-alert .alert-button:hover) {
  background-color: rgba(2, 136, 209, 0.1);
}
</style>